import React from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { TableMapDraft } from '../context';
import { useTableMapWizard } from '../context';
import CanvasStepLayout, { CanvasStepHeading } from '../components/CanvasStepLayout';
import TableMapPreview from '../components/TableMapPreview';

const GREEN = theme.palette.success.main;
const GREEN_BG = theme.palette.success.light;
const WARNING_COLOR = theme.palette.warning.main;
const WARNING_BG = '#fff8e1';

const useStyles = makeStyles({
    // One child pinned to the bottom of the toolbar slot, so the heading sits on the canvas with its line.
    slotContent: {
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    cardHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    summaryCard: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    summaryCardHeader: {
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        backgroundColor: theme.palette.common.white,
    },
    summaryRows: {
        padding: '8px 0',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    summaryValue: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: 'right' as const,
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    readyBlock: {
        margin: 16,
        padding: '14px 16px',
        borderRadius: 8,
        backgroundColor: GREEN_BG,
        border: `1px solid ${GREEN}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
    },
    readyIcon: {
        color: GREEN,
        fontSize: 22,
        flexShrink: 0,
        marginTop: 1,
    },
    readyTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: GREEN,
        marginBottom: 2,
    },
    readyDesc: {
        fontSize: 12,
        color: GREEN,
        lineHeight: 1.4,
    },
    notReadyBlock: {
        margin: 16,
        padding: '14px 16px',
        borderRadius: 8,
        backgroundColor: WARNING_BG,
        border: `1px solid ${WARNING_COLOR}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
    },
    notReadyIcon: {
        color: WARNING_COLOR,
        fontSize: 22,
        flexShrink: 0,
        marginTop: 1,
    },
    notReadyTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: WARNING_COLOR,
        marginBottom: 2,
    },
    notReadyReasonText: {
        fontSize: 12,
        color: WARNING_COLOR,
        lineHeight: 1.5,
    },
});

export function isReviewReady(draft: TableMapDraft): boolean {
    if (draft.name.trim() === '' || draft.tables.length === 0) return false;
    if (draft.entrances.length === 0) return false;
    const names = draft.tables.map((t) => t.label.trim().toLowerCase());
    if (new Set(names).size !== names.length) return false;
    return true;
}

export default function ReviewStep() {
    const classes = useStyles();
    const { draft } = useTableMapWizard();

    const totalSeats = draft.tables.reduce((sum, t) => sum + t.seats, 0);
    const ready = isReviewReady(draft);
    const isActive = draft.isActive;

    const notReadyReasons: string[] = [];
    if (!ready) {
        if (draft.name.trim() === '') {
            notReadyReasons.push(i18n.t('tableMaps.wizard.review.notReadyMapName'));
        }
        if (draft.entrances.length === 0) {
            notReadyReasons.push(i18n.t('tableMaps.wizard.review.notReadyNoOpenings'));
        }
        if (draft.tables.length === 0) {
            notReadyReasons.push(i18n.t('tableMaps.wizard.review.notReadyNoTables'));
        } else {
            const names = draft.tables.map((t) => t.label.trim().toLowerCase());
            if (new Set(names).size !== names.length) {
                notReadyReasons.push(i18n.t('tableMaps.wizard.review.notReadyDuplicateNames'));
            }
        }
    }

    return (
        <CanvasStepLayout
            panelSide="right"
            toolbar={
                <div className={classes.slotContent}>
                    <CanvasStepHeading>{i18n.t('tableMaps.wizard.steps.review')}</CanvasStepHeading>
                    <Typography className={classes.hintText}>
                        {i18n.t('tableMaps.wizard.review.tableMapPreview')}
                    </Typography>
                </div>
            }
            panel={
                <div className={classes.summaryCard}>
                    <div className={classes.summaryCardHeader}>
                        <Typography className={classes.cardHeading}>
                            {i18n.t('tableMaps.wizard.review.tableMapSummary')}
                        </Typography>
                    </div>

                    <div className={classes.summaryRows}>
                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.tableMapName')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {draft.name || '—'}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.tablesCount')}
                            </Typography>
                            <Typography className={classes.summaryValue}>
                                {draft.tables.length}
                            </Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.totalSeats')}
                            </Typography>
                            <Typography className={classes.summaryValue}>{totalSeats}</Typography>
                        </div>

                        <div className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>
                                {i18n.t('tableMaps.wizard.review.status')}
                            </Typography>
                            <div className={classes.statusCell}>
                                <span
                                    className={classes.statusDot}
                                    style={{
                                        backgroundColor: isActive
                                            ? theme.palette.success.main
                                            : theme.palette.secondary.main,
                                    }}
                                />
                                <Typography className={classes.summaryValue}>
                                    {isActive
                                        ? i18n.t('tableMaps.wizard.review.active')
                                        : i18n.t('tableMaps.wizard.review.inactive')}
                                </Typography>
                            </div>
                        </div>
                    </div>

                    {ready ? (
                        <div className={classes.readyBlock}>
                            <CheckCircleOutlineIcon className={classes.readyIcon} />
                            <div>
                                <Typography className={classes.readyTitle}>
                                    {i18n.t('tableMaps.wizard.review.readyToCreate')}
                                </Typography>
                                <Typography className={classes.readyDesc}>
                                    {i18n.t('tableMaps.wizard.review.readyToCreateDesc')}
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        <div className={classes.notReadyBlock}>
                            <WarningIcon className={classes.notReadyIcon} />
                            <div>
                                <Typography className={classes.notReadyTitle}>
                                    {i18n.t('tableMaps.wizard.review.notReady')}
                                </Typography>
                                {notReadyReasons.map((reason, idx) => (
                                    <Typography key={idx} className={classes.notReadyReasonText}>
                                        {`• ${reason}`}
                                    </Typography>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            }
        >
            <TableMapPreview draft={draft} />
        </CanvasStepLayout>
    );
}
